// The Termos and the Política de privacidade (SPEC-14). Drafts: a banner
// says so, and each point the founder has not decided yet shows as a
// "Por decidir" block rather than as a promise.
//
// SPEC-31: they exist in PT only. In another language the page is marked
// PT, and a note in the app's language says so at its top.

import { HTML_LANG } from "../i18n/language";
import { useCopy, useLanguage } from "../i18n/LanguageProvider";
import { copy } from "../i18n/pt-PT";
import type { LegalBlock, LegalSection } from "../i18n/pt-PT";
import { paths, useRouter } from "../lib/router";

function Block({ block }: { block: LegalBlock }) {
  if ("text" in block) return <p className="text-body leading-[var(--lh-body)]">{block.text}</p>;
  return (
    <p className="rounded-card-inner bg-orange-soft px-4 py-3 text-body-sm leading-[var(--lh-body)] text-orange-ink">
      <strong className="font-semibold">{copy.legal.todoLabel}: </strong>
      {block.todo}
    </p>
  );
}

export function Legal({ document }: { document: "terms" | "privacy" }) {
  const { back } = useRouter();
  const { language } = useLanguage();
  const notice = useCopy().legalNotice;
  const { title, sections }: { title: string; sections: readonly LegalSection[] } = copy.legal[document];
  const translated = language !== "pt";

  return (
    <main lang={translated ? HTML_LANG.pt : undefined} className="screen-enter flex min-h-dvh flex-col gap-6 bg-cream px-5 pt-5 pb-12">
      <button
        type="button"
        onClick={() => back(paths.welcome)}
        className="flex size-[var(--touch-min)] items-center justify-center rounded-glyph border border-line-10 bg-white text-title text-dark"
      >
        <span aria-hidden="true">←</span>
        <span className="sr-only">{copy.legal.back}</span>
      </button>

      <header className="flex flex-col gap-3">
        {translated && (
          <p role="note" lang={HTML_LANG[language]} className="rounded-card-inner bg-violet-tint px-4 py-3 text-body-sm font-semibold text-violet-ink">
            {notice.onlyPortuguese}
          </p>
        )}
        <h1 className="text-display leading-[var(--lh-display)] font-semibold tracking-[var(--ls-display)] text-pretty">
          {title}
        </h1>
        <p className="text-caption text-muted">{copy.legal.version}</p>
        <p role="note" className="rounded-card-inner bg-violet-tint px-4 py-3 text-body-sm text-violet-ink">
          {copy.legal.draft}
        </p>
      </header>

      {sections.map((section) => (
        <section key={section.heading} className="flex flex-col gap-3">
          <h2 className="text-title leading-[var(--lh-title)] font-semibold tracking-[var(--ls-title)]">
            {section.heading}
          </h2>
          {section.blocks.map((block) => (
            <Block key={"text" in block ? block.text : block.todo} block={block} />
          ))}
        </section>
      ))}
    </main>
  );
}
